import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ProfilePicture } from '@getro/rombo';
import { Linkedin } from 'lucide-react';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import ArrowLine from 'components/atoms/arrowLine';
import { useConnectionPathCard } from './hooks/useConnectionPathCard';

export const ConnectionPathCard = ({ item }) => {
  const { viaLabel, isTeamMember, badgeText, viaLinkedinUrl, subline, nodesForRender } = useConnectionPathCard({
    item,
  });

  const pathVisual = (
    <Flex sx={{ alignItems: 'center', gap: '8px' }}>
      {nodesForRender.map((node) => (
        <Flex key={node.key} sx={{ alignItems: 'flex-start', gap: '8px' }}>
          <Flex sx={{ alignItems: 'center', flexDirection: 'column', width: '96px', gap: '4px' }}>
            <Box
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: 'neutral.100',
                display: 'flex',
              }}
            >
              <ProfilePicture width="48px" height="48px" name={node.avatarName} imageUrl={node.avatarUrl} />
            </Box>
            <Text fontWeight="500" color="text.dark" fontSize="12px" lineHeight="16px" sx={{ textAlign: 'center' }}>
              {node.labelName}
            </Text>
          </Flex>
          {!node.isLast && <ArrowLine color="purple.300" width="33px" height="8px" sx={{ marginTop: '20px' }} />}
        </Flex>
      ))}
    </Flex>
  );

  return (
    <Flex
      sx={{
        borderBottom: '1px solid',
        borderColor: 'border.subtle',
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        p: '8px 0',
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <Flex sx={{ alignItems: 'center', gap: '4px', minWidth: 0, flexWrap: 'nowrap' }}>
          <Text fontWeight="600" color="text.dark" fontSize="14px">
            Via
          </Text>
          <Flex sx={{ alignItems: 'center', gap: '8px', minWidth: 0, flexWrap: 'nowrap' }}>
            <Text
              fontWeight="600"
              color="text.dark"
              fontSize="14px"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {viaLabel}
            </Text>
            {!isTeamMember && viaLinkedinUrl && (
              <DynamicTooltip
                position="top-end"
                content={
                  <Text fontSize="14px" color="neutral.0">
                    Open LinkedIn profile
                  </Text>
                }
                trigger={
                  <Flex
                    as="a"
                    href={viaLinkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Box as={Linkedin} size={14} sx={{ color: 'text.subtle' }} />
                  </Flex>
                }
              />
            )}
            <Text
              as="span"
              sx={{
                fontSize: '10px',
                letterSpacing: '.5px',
                textTransform: 'uppercase',
                fontWeight: '600',
                color: isTeamMember ? 'purple.500' : 'text.main',
                backgroundColor: isTeamMember ? 'purple.100' : 'neutral.50',
                borderRadius: '4px',
                px: '8px',
                py: '2px',
                flexShrink: 0,
              }}
            >
              {badgeText}
            </Text>
          </Flex>
        </Flex>
        <Text color="text.subtle" fontSize="14px" sx={{ minHeight: '18px' }}>
          {typeof subline === 'string' || !subline ? (
            subline
          ) : (
            <>
              {subline.lines?.[0]}
              {subline.lines?.[1] && (
                <>
                  <br />
                  {subline.lines[1]}
                </>
              )}
            </>
          )}
        </Text>
      </Flex>

      <Flex sx={{ alignItems: 'center', gap: '8px', flexShrink: 0 }}>{pathVisual}</Flex>
    </Flex>
  );
};

ConnectionPathCard.propTypes = {
  item: PropTypes.object.isRequired,
};
